/**
 * parts.js — the small controls the Closing tab repeats at all three levels
 * (section · sub-section · item).
 */
import React, { useState } from 'react';
import { useLists } from '../../context';
import { useLang } from '../../i18n';
import { useListNames } from '../../hooks/useListNames';
import { useDraft } from '../../hooks/useDraft';
import { keepInView } from '../shared/dom';

/** Inline-editable name (edit mode): saves on blur / Enter, Esc puts it back. */
export function InlineName({ value, onSave, className, ariaLabel }) {
  const [draft, setDraft] = useDraft(value);
  const commit = () => { const v = draft.trim(); if (v && v !== value) onSave(v); else setDraft(value); };
  return (
    <input className={className} value={draft} aria-label={ariaLabel}
      onChange={e => setDraft(e.target.value)} onFocus={keepInView} onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') { setDraft(value); e.currentTarget.blur(); }
      }} />
  );
}

/** Which Today list a section / sub-section / item feeds (a since-deleted list still shows while chosen). */
export function TargetSelect({ value, onChange, ariaLabel }) {
  const { active, catById } = useLists();
  const { short } = useListNames();
  const opts = active.some(c => c.id === value) ? active : [...active, catById(value)];
  return (
    <select className="do-cl__target" value={value} aria-label={ariaLabel}
      onChange={e => onChange(e.target.value)} onClick={e => e.stopPropagation()}>
      {opts.map(c => <option key={c.id} value={c.id}>{c.icon} {short(c)}</option>)}
    </select>
  );
}

/** Reorder ▲▼ + delete 🗑. */
export function EditTools({ onUp, onDown, canUp, canDown, onDelete, deleteLabel }) {
  const { t } = useLang();
  return (
    <>
      <button className="do-cl__icon-btn" onClick={onUp} disabled={!canUp} aria-label={t('closing.moveUp')}>▲</button>
      <button className="do-cl__icon-btn" onClick={onDown} disabled={!canDown} aria-label={t('closing.moveDown')}>▼</button>
      <button className="do-cl__icon-btn do-cl__icon-btn--danger" onClick={onDelete} aria-label={deleteLabel}>🗑</button>
    </>
  );
}

/**
 * Tri-state select box on a section / sub-section header (edit mode): empty → none of its
 * items picked, "–" → some, "✓" → all. Tapping picks all (or, when all, none).
 */
export function SelectBox({ list, selected, onToggle, label }) {
  const n = list.reduce((c, i) => c + (selected.has(i.id) ? 1 : 0), 0);
  const state = n === 0 ? 'none' : n === list.length ? 'all' : 'some';
  return (
    <button className={`do-cl__hcheck do-cl__hcheck--${state}`} role="checkbox" onClick={onToggle}
      disabled={list.length === 0} aria-checked={state === 'all' ? 'true' : state === 'some' ? 'mixed' : 'false'} aria-label={label}>
      <span className="do-cl__box">{state === 'all' ? '✓' : state === 'some' ? '–' : ''}</span>
    </button>
  );
}

/** "＋ Add …" row at the end of a sub-section (edit mode). */
export function AddRow({ placeholder, onAdd }) {
  const { t } = useLang();
  const [v, setV] = useState('');
  const submit = () => { const clean = v.trim(); if (clean) { onAdd(clean); setV(''); } };
  return (
    <div className="do-cl__additem">
      <input className="do-cl__additem-input" value={v} placeholder={placeholder} onFocus={keepInView}
        onChange={e => setV(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') submit(); }} aria-label={placeholder} />
      <button className="do-cl__additem-btn" onClick={submit} disabled={!v.trim()}>{t('common.add')}</button>
    </div>
  );
}
